export const MAX_INDICATOR_DAYS = 30;

export const FUNDING_METHOD_LABELS: Record<string, string> = {
  STANDARD_NOTIONAL: 'Standard Notional',
  PREFUND_SPENDDOWN: 'Prefund Spenddown',
  PREFUND_AND_CONTRIBUTION: 'Prefund and Contribution',
  CONTRIBUTION_ONLY: 'Contribution Only',
};

export const getFundingMethodLabel = (fundingMethod?: string): string | undefined => {
  if (!fundingMethod) {
    return undefined;
  }
  return FUNDING_METHOD_LABELS[fundingMethod] || fundingMethod;
};

/**
 * Formats a "days until" value into the "in X days" / "Today" indicator text.
 * Returns undefined when the event is in the past, more than MAX_INDICATOR_DAYS away,
 * or not the nearest upcoming event.
 */
export const formatDaysUntilIndicator = (daysUntil?: number): string | undefined => {
  if (daysUntil === undefined || daysUntil === null) {
    return undefined;
  }
  if (daysUntil < 0 || daysUntil > MAX_INDICATOR_DAYS) {
    return undefined;
  }
  if (daysUntil === 0) {
    return 'Today';
  }
  return daysUntil === 1 ? 'in 1 day' : `in ${daysUntil} days`;
};

interface DeadlineEvent {
  key: string;
  daysUntil?: number;
}

/**
 * Determines which deadline events (by key) should display the "in X days" indicator.
 * Only the nearest upcoming event(s) within MAX_INDICATOR_DAYS are eligible;
 * multiple events sharing the nearest date all get the indicator.
 */
export const getNearestDeadlineKeys = (events: DeadlineEvent[]): Set<string> => {
  const upcoming = events.filter(
    (event) => event.daysUntil !== undefined && event.daysUntil !== null && event.daysUntil >= 0
  );
  if (!upcoming.length) {
    return new Set();
  }
  const minDays = Math.min(...upcoming.map((event) => event.daysUntil as number));
  if (minDays > MAX_INDICATOR_DAYS) {
    return new Set();
  }
  return new Set(upcoming.filter((event) => event.daysUntil === minDays).map((event) => event.key));
};
